import React, { useEffect, useState, useRef } from 'react';
import useMediaQuery from '../../../shared/hooks/useMediaQuery';
import { useLocation, useNavigate, NavLink } from 'react-router-dom';
import styles from './Header.module.css';
import { useAuth } from '../../../contexts/AuthContext';
import { NAV_ITEMS } from '../../../constants/navigation';
import Logo from '../../common/Logo/Logo';
import { getMyTalentProfile } from '../../../api/talentApi';
import { getMyProducerProfile } from '../../../api/producerApi';
import { getDefaultProfileImage, isTalentProfileImageRemoved, resolveProfileImageUrl } from '../../../shared/utils/profilePlaceholders';

function Header({ hideLogo = false, isSidebarPage = false, onMenuClick }) {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoggedIn, role, userName, userStageName, userProfileImage, setUserInfo, logout } = useAuth();
  const [dropOpen, setDropOpen] = useState(false);
  const dropRef = useRef(null);

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    if (!dropOpen) return;
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropOpen]);

  // 로그인 상태인데 이름이나 이미지가 없으면 프로필 자동 조회 (페이지 새로고침 후 복원)
  useEffect(() => {
    if (!isLoggedIn) return;
    // 삭제 플래그가 있으면 즉시 디폴트로 초기화
    if (role === 'TALENT' && isTalentProfileImageRemoved()) {
      if (userProfileImage) setUserInfo({ profileImage: null });
      return;
    }
    // 이름도 있고 이미지도 있으면 스킵
    if (userName && userProfileImage) return;
    if (role === 'TALENT') {
      getMyTalentProfile()
        .then((p) => {
          if (p?.name) {
            const profileImage = resolveProfileImageUrl(p) || null;
            setUserInfo({ name: p.stageName || p.name, profileImage, stageName: p.stageName || null, realName: p.name || null });
          }
        })
        .catch((err) => {
          if (err?.response?.status === 401) { logout(); navigate('/login'); }
          // 404: 프로필 미생성 신규 유저 — 무시
        });
    } else if (role === 'PRODUCER') {
      getMyProducerProfile()
        .then((p) => {
          if (p?.companyName) setUserInfo({ name: p.companyName, profileImage: p.logoUrl || p.profileImage || p.imageUrl || getDefaultProfileImage('PRODUCER') });
        })
        .catch((err) => {
          if (err?.response?.status === 401) { logout(); navigate('/login'); }
        });
    }
  }, [isLoggedIn, role, userName, userProfileImage, setUserInfo, logout, navigate]);

  // 랜딩 페이지만 검은 배경(white 텍스트), 나머지는 흰 배경(dark 텍스트)
  const isLanding = location.pathname === '/';
  const variant = isLanding ? 'dark' : 'white';
  const btnClass = isLanding ? styles.loginBtnLight : styles.loginBtnDark;

  const dashboardPath = role === 'TALENT' ? '/talent/dashboard' : '/producer/dashboard';
  // 예명 있으면 예명만, 없으면 본명
  const displayName = userStageName || userName || '내 프로필';

  // 모바일 사이드바 페이지에서는 프로필을 누르면 사이드바가 열린다.
  // 드롭다운의 대시보드·로그아웃은 사이드바 안에 이미 있어 잃는 것이 없다.
  const opensDrawerOnTap = isMobile && isSidebarPage && Boolean(onMenuClick);
  const resolvedProfileImage =
    (role === 'TALENT' && isTalentProfileImageRemoved())
      ? getDefaultProfileImage('TALENT')
      : (userProfileImage || (isLoggedIn ? getDefaultProfileImage(role) : null));
  const isDefaultProfileImage =
    isLoggedIn &&
    Boolean(role) &&
    resolvedProfileImage === getDefaultProfileImage(role);

  const handleMypageClick = () => {
    navigate(isLoggedIn ? dashboardPath : '/login');
  };

  const handleLoginClick = () => navigate('/login');

  const headerClass = `
    ${styles.header} 
    ${styles[variant]} 
    ${isSidebarPage ? styles.sidebarPageHeader : ''}
  `.trim();

  return (
    <header className={headerClass}>
      <div className={styles.container}>
        {/* 로고 또는 로고 자리 예약 */}
        {hideLogo ? (
          <div className={styles.logoPlaceholder} />
        ) : (
          <Logo className={styles.logo} onClick={() => navigate('/')} />
        )}

        {/* 사이드바 페이지 모바일 메뉴 열기.
            로그인 상태에서는 오른쪽 프로필 이미지가 이 역할을 하므로 햄버거를 두지 않는다. */}
        {isSidebarPage && onMenuClick && !isLoggedIn && (
          <button className={styles.hamburger} onClick={onMenuClick} aria-label="메뉴 열기">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        )}

        {/* 우측: 메뉴 + 로그인/프로필 버튼 */}
        <div className={styles.right} style={hideLogo ? { marginLeft: 'auto' } : {}}>
          <nav className={styles.menu}>
            {NAV_ITEMS.map((item) => {
              if (item.id === 'mypage') {
                return (
                  <button
                    key={item.id}
                    className={styles.navItem}
                    onClick={handleMypageClick}
                  >
                    {item.label}
                  </button>
                );
              }
              return (
                <NavLink
                  key={item.id}
                  to={item.path}
                  className={({ isActive }) =>
                    `${styles.navItem} ${isActive ? styles.active : ''}`
                  }
                >
                  {item.label}
                </NavLink>
              );
            })}
          </nav>

          {isLoggedIn ? (
            <div className={styles.profileWrap} ref={dropRef}>
              <button
                className={styles.profileBtn}
                aria-label={opensDrawerOnTap ? '메뉴 열기' : '내 계정'}
                onClick={() => (opensDrawerOnTap ? onMenuClick() : setDropOpen(p => !p))}
              >
                <div className={styles.profileAvatar}>
                  <img
                    src={resolvedProfileImage}
                    alt="프로필"
                    className={styles.profileImg}
                    width={36}
                    height={36}
                    style={isDefaultProfileImage ? { width: '24px', height: '24px', objectFit: 'contain', display: 'block', margin: '6px auto' } : undefined}
                  />
                </div>
              </button>
              {dropOpen && !opensDrawerOnTap && (
                <div className={styles.dropdown}>
                  <button className={styles.dropItem} onClick={() => { setDropOpen(false); navigate(dashboardPath); }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" stroke="#090909" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M9 22V12h6v10" stroke="#090909" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    대시보드
                  </button>
                  <div className={styles.dropDivider} />
                  <button className={`${styles.dropItem} ${styles.dropItemDanger}`} onClick={async () => { setDropOpen(false); await logout(); navigate('/'); }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" stroke="#EF4444" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M16 17l5-5-5-5M21 12H9" stroke="#EF4444" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    로그아웃
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button className={`${styles.loginBtn} ${btnClass}`} onClick={handleLoginClick}>
              로그인
            </button>
          )}
        </div>
      </div>
    </header>
  );
}

export default Header;
